<script setup lang="ts">
import type { AccountProtectionAccordions } from '@/types/UserLimits';

type Emits = {
  (e: 'continue'): void;
};

const emit = defineEmits<Emits>();

const { t } = useTexts();
const { forceOpenAccordion } = useAccountProtection();
const { openSupportChat, hasLiveSupport } = useLiveSupport();

const alternativeItems = [
  {
    title: t('account.account_protection.pause_7_days'),
    action: 'accordion-timeout',
  },
  {
    title: t('account.account_protection.pause_30_days'),
    action: 'accordion-timeout',
  },
  {
    title: t('account.account_protection.keep_active_with_bet_limit'),
    action: 'accordion-bet',
  },
  {
    title: t('account.account_protection.keep_active_with_deposit_limit'),
    action: 'accordion-deposit',
  },
];

if (hasLiveSupport.value) {
  alternativeItems.push({
    title: t('account.account_protection.talk_to_support'),
    action: 'support',
  });
}

function handleClick(item: string) {
  if (item === 'support') {
    openSupportChat();
  } else {
    forceOpenAccordion(item as AccountProtectionAccordions);
  }
}
</script>

<template>
  <div :class="$style.exclusionAlternatives">
    <div :class="$style.title">
      <span v-html="$t('account.account_protection.alternative_options')" />
      <span :class="$style.subtitle">
        {{ t('account.account_protection.would_you_like_to_evaluate') }}
      </span>
    </div>

    <div :class="$style.items">
      <button
        v-for="item in alternativeItems"
        :key="item.action"
        :class="$style.item"
        @click="handleClick(item.action)"
      >
        <span :class="$style.itemTitle"> {{ item.title }} </span>

        <NuxtIcon
          :class="$style.itemIcon"
          name="arrows/arrow_right"
        />
      </button>

      <button
        :class="[$style.item, $style.continue]"
        @click="emit('continue')"
      >
        <span :class="$style.itemTitle">
          {{ t('account.account_protection.continue_with_permanent_block') }}
        </span>

        <NuxtIcon
          :class="$style.itemIcon"
          name="close"
        />
      </button>
    </div>
  </div>
</template>

<style module lang="postcss">
.exclusionAlternatives {
  @apply flex flex-col gap-5 w-full border border-dashed border-texts/40 rounded-lg py-5 px-6;

  .title {
    @apply text-center text-sm text-texts flex flex-col justify-start items-start;

    .subtitle {
      @apply text-base font-medium text-warning;
    }
  }

  .items {
    @apply flex flex-col gap-2;

    .item {
      @apply inline-flex items-center justify-between gap-5 bg-texts/5 text-texts/90 py-3 px-4 rounded-lg cursor-pointer;

      .itemTitle {
        @apply text-sm;
      }

      .itemIcon {
        @apply text-xs;
      }

      &.continue {
        @apply opacity-60 text-texts/60;

        &:hover {
          @apply opacity-50;
        }
      }
    }
  }
}
</style>
